/* eslint-disable jsx-a11y/anchor-is-valid */
/** @jsxImportSource @emotion/react */
import 'twin.macro';
import tw from 'twin.macro';
import { css } from '@emotion/react';
import { FilterTextBox } from './FilterTextBox';
import {
  AdvancedFilterType,
  CollectionType,
  SearchField,
  SortField,
} from 'utils/enum';
import { FilterOption } from './FilterOption';
import { isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'reducers';
import { FilterInput } from './FilterInput';
import { FilterCustomDate } from './FilterCustomDate';
import { ReactComponent as ClearSvg } from 'assets/Icons/x-cancel-red.svg';
import { ReactComponent as CloseSVG } from 'assets/Icons/speaker-remove.svg';
import { TextInput } from 'components/shared/TextInput';
import { Ripple } from '@rmwc/ripple';
import { useToggle } from 'react-use';
import {
  resetAdvancedSearch,
  setSearchField,
  setSearchParams,
  toggleSearchClipsMode,
  addExcludeWord,
  removeExcludeWord,
  addLanguage,
  removeLanguage,
} from 'slices/advanced-search.slice';
import { Hint } from 'components/shared/Hint';
import { Switcher } from 'components/shared/Switcher';
import { changeSortBy, setPageNumber } from 'slices/pagination.slice';
import { handleSearchClear, setSelectedRows, setSelectMode } from 'actions';
import { toggleReloadLibrary } from 'slices/toggle.slice';
import { CustomSelectSearch } from 'components/shared/CustomSelectSearch';
import { GroupBase, StylesConfig } from 'react-select';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { SpeakerDiarisation } from 'utils/models';
import { useHistory } from 'react-router-dom';
import { setSelectedClips, toggleSelectMode } from 'slices/clips.slice';
import {
  applyCollectionFilters,
  clearIncludeCollectionFilters,
  setPartialCollectionFilters,
  resetSelectedCollectionId,
} from 'slices/collection.slice';
import { Option } from 'components/shared/CustomSelectSearch';
import {
  isCollectionPage,
  isLibraryPage,
} from 'components/VideoPlayer/Transcription/MediaUtilities';
import { ARCHIVE_TAG_NAME } from 'utils/constants';
import {
  SEMANTIC_LANGUAGES,
  semanticLanguageLabel,
} from '../semanticSearchQuery';
import { filterSupport, pickSupportedFilters } from '../filterSupport';

const tagLayers: { name: string; field: SearchField }[] = [
  { name: 'Key terms', field: SearchField.KEYWORD },
  { name: 'IAB categories', field: SearchField.IAB },
  { name: 'People', field: SearchField.PEOPLE },
  { name: 'Products', field: SearchField.PRODUCT },
  { name: 'Organisations', field: SearchField.ORG },
  { name: 'Locations', field: SearchField.LOCATION },
  { name: 'Nationalities', field: SearchField.NATIONALITY },
];

export const FilterBar = () => {
  const dispatch = useDispatch();

  const [newSpeakerName, setNewSpeakerName] = useState('');
  const [, setNewCollectionName] = useState('');
  const [isTagOpen, setIsTagOpen] = useState(false);
  // excludeWords/languages live in Redux (advanced-search.slice) so
  const [isAddingExcludeWord, toggleAddingExcludeWord] = useToggle(false);
  const [tagPos, setTagPos] = useState<{
    top: number;
    left: number;
    minWidth: number;
  }>({ top: 0, left: 0, minWidth: 0 });
  const tagTriggerRef = useRef<HTMLDivElement>(null);

  const keywordContentRef = useRef<HTMLUListElement>(null);
  const history = useHistory();

  const advancedSearch = useSelector(
    (state: RootState) => state.advancedSearch,
  );
  const searchParams = advancedSearch.searchParams;
  // Both modes share one filter pool; each shows only the options it supports.
  const support = filterSupport(advancedSearch.isSemanticSearch);
  const visibleTagLayers = tagLayers.filter(({ field }) =>
    support.searchFields.includes(field),
  );
  // Includes chips typed under a field this mode lacks stay in the pool but
  // are hidden here, as they are not sent.
  const visibleSearchValues = pickSupportedFilters(
    searchParams,
    advancedSearch.isSemanticSearch,
  ).searchValues;
  const semanticXlHeader =
    advancedSearch.isSemanticSearch && tw`xl:(width[14rem])!`;
  const speakerIdentification = useSelector(
    (state: RootState) => state.speakerIdentification,
  );
  const collection = useSelector((state: RootState) => state.collection);

  const tag = useSelector((state: RootState) => state.tags);

  const currentCollection = useMemo(() => {
    const tagArchive = tag.activeTags.find(
      (tag) => tag.tagName === ARCHIVE_TAG_NAME,
    );

    const isFilterArchiveTag = collection.filters.tagIds.includes(
      tagArchive?.tagId ?? '',
    );

    const visibleCollection = isFilterArchiveTag
      ? collection.visibleCollection
      : collection.visibleCollection.filter((tag) => !tag.isArchived);

    const collectionItems = isFilterArchiveTag
      ? collection.items
      : collection.items.filter((tag) => !tag.isArchived);

    if (!isEmpty(collection.visibleCollection)) {
      return visibleCollection;
    }

    return collectionItems;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    collection.visibleCollection,
    collection.items,
    collection.filters.tagIds,
  ]);

  const modeCollectionChips: { id: string; name: string }[] =
    collection.filters.collections;

  const showReadOnlyCollectionFilterOnCollectionPage =
    isCollectionPage() &&
    !isEmpty(collection.selectedId) &&
    modeCollectionChips.some((c) => c.id === collection.selectedId);

  const shouldHideCollectionFilter =
    (isCollectionPage() &&
      !isEmpty(collection.selectedId) &&
      !showReadOnlyCollectionFilterOnCollectionPage) ||
    (isLibraryPage() && advancedSearch.isShowClipsOnly);

  useEffect(() => {
    if (!shouldHideCollectionFilter) return;
    if (isEmpty(collection.filters.collectionIds)) return;
    if (!isEmpty(collection.filters.tagIds)) return;

    dispatch(
      setPartialCollectionFilters({
        collectionIds: [],
      }),
    );
  }, [
    shouldHideCollectionFilter,
    collection.filters.collectionIds,
    dispatch,
  ]);

  useEffect(() => {
    if (!isTagOpen) return;
    const rect = tagTriggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setTagPos({
      top: rect.bottom + window.scrollY + 4,
      left: rect.left + window.scrollX,
      minWidth: rect.width,
    });
  }, [isTagOpen]);

  useEffect(() => {
    if (!isTagOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const isClickOnTrigger = tagTriggerRef.current?.contains(target);
      const isClickOnDropdown = keywordContentRef.current?.contains(target);

      if (!isClickOnTrigger && !isClickOnDropdown) {
        setIsTagOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isTagOpen]);

  const currentSearchField = (): string =>
    visibleTagLayers.find(
      (layer) => layer.field === advancedSearch.searchParams.searchField,
    )?.name ?? '';

  const handleClearFilter = () => {
    if (!advancedSearch.isSemanticSearch) {
      dispatch(handleSearchClear());
    }
    dispatch(resetAdvancedSearch());
    dispatch(clearIncludeCollectionFilters());
    dispatch(applyCollectionFilters());
    dispatch(setPageNumber(1));
    dispatch(toggleReloadLibrary());
  };

  const collectionOptions: Option[] = (
    !isEmpty(collection.filters.tagIds)
      ? collection.tagFilters.collections
      : currentCollection
  ).map((item) => ({
    label: item.name,
    value: item.id,
  }));

  const handleChangeCollections = (newValues: Option | null) => {
    if (!newValues) {
      return;
    }

    const isExistedCollection = collection.filters.collections.some(
      (collection) => collection.id === newValues.value,
    );
    if (isExistedCollection) {
      return;
    }

    const shouldResetCollectionIds =
      !isEmpty(collection.filters.tagIds) &&
      isEmpty(collection.filters.collections);

    const existingCollectionIds = shouldResetCollectionIds
      ? []
      : collection.filters.collectionIds;

    dispatch(
      setPartialCollectionFilters({
        collectionIds: [newValues.value, ...existingCollectionIds],
        collections: [
          {
            id: newValues.value,
            name: newValues.label,
            type: CollectionType.STANDARD,
          },
          ...collection.filters.collections,
        ],
      }),
    );
    dispatch(setPageNumber(1));
  };

  const handleChangeSearchValue = (value: string) => {
    setNewSpeakerName(value);
  };

  const handleChangeCollectionSearchValue = (value: string) => {
    setNewCollectionName(value);
  };

  const addSpeakerName = (name: string) => {
    const payload = { type: AdvancedFilterType.SPEAKERS, data: name };
    dispatch(setSearchParams(payload));
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      const isExistedName = speakerIdentification.speakers.some((speaker) =>
        speaker?.speakerName
          ?.toLowerCase()
          ?.includes(newSpeakerName?.trim()?.toLowerCase()),
      );

      if (isExistedName) {
        event.preventDefault();
      }

      addSpeakerName(newSpeakerName);
    }
  };

  return (
    <div tw="flex">
      {!advancedSearch.isSemanticSearch && <div tw="flex-1"></div>}
      <div
        tw="flex relative px-5 text-xl mr-8 py-5 pb-0 border-bottom[1px solid]"
        css={
          // Semantic wraps its columns; a wrapped row starts at the left
          // instead of centering under the row above.
          advancedSearch.isSemanticSearch
            ? tw`flex-1 min-width[0] flex-wrap justify-start gap-x-6 gap-y-4 ml-8`
            : tw`justify-center space-x-6 width[65%] xl:width[72%]`
        }
      >
        {/* UPLOAD DATE */}
        <div css={childStyle}>
          <div css={headerStyle}>Upload date</div>
          <hr css={hrStyle} />
          {support.uploadDates.map((period, index) => (
            <FilterOption
              typeEnum={AdvancedFilterType.DATE}
              key={index}
              textEnum={period}
            />
          ))}

          {support.customDate && <FilterCustomDate />}
        </div>
        {/* TYPE */}
        <div css={childStyle}>
          <div css={headerStyle}>Type</div>
          <hr css={hrStyle} />
          {support.types.map((media, index) => (
            <FilterOption
              typeEnum={AdvancedFilterType.TYPE}
              key={index}
              textEnum={media}
            />
          ))}

          <div
            css={[
              headerStyle,
              advancedSearch.isSemanticSearch ? tw`mt-2` : tw`mt-5`,
            ]}
          >
            Search by
          </div>
          <hr css={hrStyle} />
          <div tw="margin-top[4px]">
            {support.searchBy.map((searchBy) => (
              <FilterOption
                typeEnum={AdvancedFilterType.TITLE}
                key={searchBy}
                textEnum={searchBy}
              />
            ))}
          </div>
          {!isEmpty(support.matchTypes) && (
            <>
              <div tw="mt-2" css={headerStyle}>
                Match type
              </div>
              <hr css={hrStyle} />
              <div tw="margin-top[4px]">
                {support.matchTypes.map((matchType) => (
                  <FilterOption
                    typeEnum={AdvancedFilterType.MATCH_TYPE}
                    key={matchType}
                    textEnum={matchType}
                  />
                ))}
              </div>
            </>
          )}
        </div>
        {/* DURATION — hidden for Semantic search: no duration filter in that engine */}
        {!isEmpty(support.durations) && (
          <div css={childStyle}>
            <div css={headerStyle}>Duration</div>
            <hr css={hrStyle} />
            {support.durations.map((duration) => (
              <FilterOption
                typeEnum={AdvancedFilterType.DURATION}
                key={duration}
                textEnum={duration}
              />
            ))}
          </div>
        )}
        {/* COLLECTIONS */}
        {!shouldHideCollectionFilter && (
          <div css={childStyle}>
            <div css={[headerStyle, tw`(width[18rem] flex items-center)!`]}>
              <span>Includes: Collection</span>
            </div>
            <hr css={hrStyle} />
            <div>
              <div tw="max-height[9.9rem]!" css={[customInputContainer]}>
                {(showReadOnlyCollectionFilterOnCollectionPage
                  ? modeCollectionChips.filter(
                      (c) => c.id === collection.selectedId,
                    )
                  : modeCollectionChips
                ).map((collections) => (
                  <FilterTextBox
                    typeEnum={AdvancedFilterType.COLLECTION}
                    key={collections.id}
                    text={collections.name}
                    readOnly={showReadOnlyCollectionFilterOnCollectionPage}
                  />
                ))}
              </div>

              {!showReadOnlyCollectionFilterOnCollectionPage && (
                <div
                  tw="my-2"
                  css={[
                    isEmpty(collection.filters.collectionIds) && tw`mt-0`,
                  ]}
                >
                  <CustomSelectSearch
                    options={collectionOptions}
                    canCreate={false}
                    defaultValue={'+ Add collection name'}
                    handleChangeSearchValue={handleChangeCollectionSearchValue}
                    // handleKeyDown={handleKeyDown}
                    onChange={handleChangeCollections}
                    customStyles={customStyles}
                  />
                </div>
              )}
            </div>
          </div>
        )}
        {/* SPEAKER NAME */}
        <div css={childStyle}>
          <div css={[headerStyle, tw`(width[18rem] flex items-center)!`]}>
            <span>Includes: Speaker name</span>
          </div>
          <hr css={hrStyle} />
          <div>
            <div tw="max-height[9.9rem]!" css={[customInputContainer]}>
              {searchParams.speakerNames.map(
                (speakerName, index) => (
                  <FilterTextBox
                    typeEnum={AdvancedFilterType.SPEAKERS}
                    key={index}
                    text={speakerName}
                  />
                ),
              )}
            </div>

            <div
              tw="my-2"
              css={[
                isEmpty(searchParams.speakerNames) && tw`mt-0`,
              ]}
            >
              <CustomSelectSearch
                options={speakerIdentification.speakers
                  // hasPrompt only
                  .filter((speaker: SpeakerDiarisation) => speaker?.hasPrompt)
                  .map((item) => ({
                    label: item.speakerName,
                    value: item.speakerName,
                  }))}
                canCreate={false}
                defaultValue={'+ Add speaker name'}
                handleChangeSearchValue={handleChangeSearchValue}
                handleKeyDown={handleKeyDown}
                onChange={(newValue) => addSpeakerName(newValue.value)}
                customStyles={customStyles}
              />
            </div>
          </div>
        </div>
        {/* KEYWORDS — same Includes layers in both modes */}
        {!isEmpty(support.searchFields) && (
          <div css={childStyle}>
            <div css={[headerStyle, tw`(width[18rem] flex)!`]}>
              <span>Includes:</span>
              <div
                ref={tagTriggerRef}
                css={[customDropdownCss]}
                onClick={() => setIsTagOpen((prev) => !prev)}
              >
                <span tw="(width[unset] py-0 px-3 flex items-center text-12 whitespace-nowrap hover:(bg-sonnant-grey-1)  border-none bg-transparent cursor-pointer)!">
                  {currentSearchField()}
                  <span className="arrow-down" tw="-mt-1"></span>
                </span>
              </div>
            </div>
            <hr css={hrStyle} />
            <div>
              <div tw="max-height[9.9rem]!" css={[customInputContainer]}>
                {visibleSearchValues.map(
                  (keyword, index) => (
                    <FilterTextBox
                      typeEnum={AdvancedFilterType.CUSTOM}
                      key={index}
                      text={keyword}
                    />
                  ),
                )}
              </div>
              <FilterInput
                typeEnum={AdvancedFilterType.CUSTOM}
                displayText="+ Add filter"
                placeholder="Give a value"
              />
            </div>
          </div>
        )}
        {/* LANGUAGES — semantic-search-only (lang:/language:, no equivalent
            in the old search). Fixed list, see SEMANTIC_LANGUAGES. */}
        {!isEmpty(support.languages) && (
          <div css={childStyle}>
            <div
              css={[
                headerStyle,
                tw`(width[18rem] flex items-center)!`,
                semanticXlHeader,
              ]}
            >
              <span>Includes: Languages</span>
            </div>
            <hr css={hrStyle} />
            <div>
              <div tw="max-height[9.9rem]!" css={[customInputContainer]}>
                {searchParams.languages.map((language) => (
                  <div
                    key={language}
                    tw="flex mt-1.5 max-width[25rem] justify-between rounded-xl pl-1.5 pr-1.5 border-sonnant-blue border-width[2px] border-solid"
                  >
                    <div
                      className="line-clamp-1"
                      tw="mr-2 text-12 font-medium word-break[break-all]"
                    >
                      {semanticLanguageLabel(language)}
                    </div>
                    <span tw="mr-0.5">
                      <Hint text="Remove" notTransparent arrow>
                        <CloseSVG
                          tw="relative top[1px] cursor-pointer"
                          onClick={() => dispatch(removeLanguage(language))}
                        />
                      </Hint>
                    </span>
                  </div>
                ))}
              </div>
              <div
                tw="my-2"
                css={[
                  isEmpty(searchParams.languages) && tw`mt-0`,
                ]}
              >
                <CustomSelectSearch
                  options={SEMANTIC_LANGUAGES.filter(
                    ({ value }) =>
                      support.languages.includes(value) &&
                      !searchParams.languages.includes(value),
                  )}
                  canCreate={false}
                  defaultValue={'+ Add language'}
                  onChange={(newValue) => dispatch(addLanguage(newValue.value))}
                  customStyles={customStyles}
                />
              </div>
            </div>
          </div>
        )}
        {/* EXCLUDES — semantic-search-only: negated words (-word). */}
        {support.excludeWords && (
          <div css={[childStyle, tw`flex-none width[11rem]`]}>
            <div css={[headerStyle, tw`(width[11rem] flex items-center)!`]}>
              <span>Excludes: Words</span>
            </div>
            <hr css={hrStyle} />
            <div>
              <div tw="max-height[9.9rem]!" css={[customInputContainer]}>
                {searchParams.excludeWords.map((word) => (
                  <div
                    key={word}
                    tw="flex mt-1.5 max-width[25rem] justify-between rounded-xl pl-1.5 pr-1.5 border-sonnant-blue border-width[2px] border-solid"
                  >
                    <div
                      className="line-clamp-1"
                      tw="mr-2 text-12 font-medium word-break[break-all]"
                    >
                      {word}
                    </div>
                    <span tw="mr-0.5">
                      <Hint text="Remove" notTransparent arrow>
                        <CloseSVG
                          tw="relative top[1px] cursor-pointer"
                          onClick={() => dispatch(removeExcludeWord(word))}
                        />
                      </Hint>
                    </span>
                  </div>
                ))}
              </div>
              {isAddingExcludeWord ? (
                <div tw="px-0.5">
                  <div tw="flex justify-between border-width[2px] border-solid border-sonnant-blue rounded-xl mt-1.5">
                    <TextInput
                      tw="(border-none focus:(border-none) height[2.5rem] py-1 px-1.5 pl-2 text-13 placeholder:(text-12 font-normal) !bg-transparent font-medium)!"
                      placeholder="Give a value"
                      onPressEnter={(value) => {
                        dispatch(addExcludeWord(value.trim()));
                        toggleAddingExcludeWord(false);
                      }}
                      onPressEsc={() => toggleAddingExcludeWord(false)}
                      onBlur={(value) => {
                        dispatch(addExcludeWord(value.trim()));
                        toggleAddingExcludeWord(false);
                      }}
                    />
                  </div>
                </div>
              ) : (
                <Ripple onClick={() => toggleAddingExcludeWord(true)}>
                  <div tw="flex justify-between items-center text-13 font-medium opacity[.75] w-full cursor-pointer py-1 pl-1.5 pr-1 margin-top[0.3rem] rounded-md hover:(bg-sonnant-grey-light)">
                    + Add word
                  </div>
                </Ripple>
              )}
            </div>
          </div>
        )}
      </div>

      {isTagOpen &&
        !isEmpty(support.searchFields) &&
        createPortal(
          <ul
            ref={keywordContentRef}
            className={'manageDropDown'}
            css={tagDropdownPortalCss}
            style={{
              top: tagPos.top,
              left: tagPos.left,
              minWidth: tagPos.minWidth,
            }}
          >
            {visibleTagLayers.map((layer, index) => (
              <li
                key={layer.field}
                css={[layerItemCss]}
                onClick={() => {
                  dispatch(setSearchField(layer.field));
                  setIsTagOpen(false);
                }}
              >
                <a>{layer.name}</a>
              </li>
            ))}
          </ul>,
          document.body,
        )}

      <div tw="flex justify-center items-center min-width[21rem] flex-col mx-5">
        <div tw="flex items-center font-medium text-sonnant-red hover:(underline cursor-pointer) text-12 align-self[flex-start] mt-5 ml-2.5">
          <span tw="flex items-center mr-1">
            <ClearSvg tw="width[2rem] height[2rem]" />
          </span>
          <span
            tw="relative top[1px] focus:(font-medium)"
            onClick={handleClearFilter}
          >
            Clear filters
          </span>
        </div>
        <div tw="flex items-center text-13 font-medium border text-sonnant-grey-6 space-x-2.5 mr-3 line-height[1.2] relative h-full align-self[flex-start]">
          <span>Content item</span>
          <Hint text="Scroll transcript with play">
            <div>
              <Switcher
                checked={advancedSearch.isShowClipsOnly}
                onCheckedChange={() => {
                  dispatch(toggleSearchClipsMode());
                  dispatch(setPageNumber(1));
                  dispatch(changeSortBy(SortField.MODIFIED));

                  // reset select mode
                  dispatch(setSelectMode(false));
                  dispatch(toggleSelectMode(false));
                  dispatch(setSelectedRows([]));
                  dispatch(setSelectedClips([]));

                  dispatch(resetSelectedCollectionId());
                  history.push('/library');
                }}
              />
            </div>
          </Hint>
          <span>Clips item</span>
        </div>
      </div>
    </div>
  );
};

const customStyles: StylesConfig<Option, false, GroupBase<Option>> = {
  container: (base) => ({
    ...base,
    height: '3rem',
    width: '100%',
  }),
  indicatorsContainer: (base) => ({
    ...base,
    display: 'none',
  }),
  control: (base) => ({
    ...base,
    border: 'none',
    minHeight: '3rem',
  }),
  menu: (base) => ({
    ...base,
    marginBottom: '4px',
    marginTop: '4px',
    zIndex: 9999,
    fontSize: '12.5px',
    fontFamily:
      "'Lexend', 'Roboto', 'Arial', 'Helvetica', 'Verdana', sans-serif",
  }),
  menuList: (base) => ({
    ...base,
    overflowX: 'hidden',
    fontSize: '12.5px',
    fontFamily:
      "'Lexend', 'Roboto', 'Arial', 'Helvetica', 'Verdana', sans-serif",
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  input: (base) => ({
    ...base,
    height: '0.5rem',
    position: 'relative',
    top: '-2rem',
  }),
  valueContainer: (base) => ({
    ...base,
    padding: 0,
    paddingLeft: '0.8rem',
    height: '2rem',
  }),
  placeholder: (base) => ({
    ...base,
    color: 'rgb(5, 10, 56)',
    top: '-1px',
    position: 'relative',
    fontWeight: 500,
    opacity: 0.75,
  }),
  option: (base) => ({
    ...base,
    cursor: 'pointer',
    color: 'rgb(51, 51, 51)',
    height: '3rem',
    display: '-webkit-box',
    WebkitLineClamp: 1,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    wordBreak: 'break-word',
    padding: '4px 12px',
    fontSize: '12.5px',
    fontFamily:
      "'Lexend', 'Roboto', 'Arial', 'Helvetica', 'Verdana', sans-serif",
  }),
};

const hrStyle = css`
  ${tw`mt-0 mb-1 border[1px solid grey] max-w-none`}
`;
const childStyle = css`
  ${tw`flex-1 text-sonnant-blue-dark cursor-pointer`}
`;

const headerStyle = css`
  ${tw`font-bold text-sonnant-blue pl-1.5 whitespace-nowrap`}
`;

const customInputContainer = css`
  ${tw`max-height[13rem] overflow-x-auto px-0.5`}

  ::-webkit-scrollbar {
    width: 4px;
  }
`;

const layerItemCss = css`
  ${tw`text-13 whitespace-nowrap px-2 py-1 font-medium select-none`}

  ${tw`active:(bg-sonnant-grey-4 [a]:text-sonnant-dark)!`}
`;

const customDropdownCss = css`
  ${tw``}
`;

const tagDropdownPortalCss = css`
  position: absolute;
  border: 1px solid #d8dbe4;
  border-radius: 2px;
  padding: 4px 0;
  max-height: 16rem;
  overflow-y: auto;
  margin: 0;
  background-color: #f9f9f9;
  min-width: 200px;
  left: -12px;
  box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.2);
  z-index: 3;
`;
